import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import type { Building } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'
import { Badge } from '../../../atoms/Badge'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import { Card, CardHeader, CardTitle } from '../EnergyTab.styles'

interface SolarComparisonCardProps {
  building: Building
  t: TFunction
  className?: string
}

const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ScenarioColumn = styled.div<{ $variant: 'actual' | 'optimal' }>`
  padding: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ $variant }) =>
    $variant === 'actual'
      ? 'linear-gradient(135deg, #3b82f615 0%, #3b82f608 100%)'
      : 'linear-gradient(135deg, #f59e0b15 0%, #f59e0b08 100%)'};
  border: 2px solid
    ${({ $variant }) => ($variant === 'actual' ? '#3b82f6' : '#f59e0b')};
`

const ScenarioHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
`

const ScenarioMetric = styled.div`
  margin: 12px 0;
`

const MetricLabelSmall = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const MetricValueLarge = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`

const NoteBox = styled.div`
  margin-top: 20px;
  padding: 12px 16px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  border-radius: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.5;
`

export const SolarComparisonCard: React.FC<SolarComparisonCardProps> = ({
  building,
  t,
  className,
}) => {
  const {
    solar_area,
    solar_area_best,
    solar_global_exposure,
    solar_global_exposure_best,
    solar_electrical_efficiency,
    solar_electrical_efficiency_best,
  } = building

  const hasActualData = solar_area && solar_electrical_efficiency
  const hasOptimalData = solar_area_best && solar_electrical_efficiency_best

  if (!hasActualData && !hasOptimalData) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>
            <Icon name="sun" size="small" />
            {t('solarComparison.title')}
          </CardTitle>
        </CardHeader>
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          {t('solarComparison.noData')}
        </div>
      </Card>
    )
  }

  // Estimate capacity: 1 kWp requires ~6.5 m²
  const estimatedCapacityActual = solar_area ? solar_area / 6.5 : 0
  const estimatedCapacityOptimal = solar_area_best ? solar_area_best / 6.5 : 0

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <Icon name="sun" size="small" />
          {t('solarComparison.titleActualVsOptimal')}
        </CardTitle>
      </CardHeader>

      <ComparisonContainer>
        {/* Actual Conditions */}
        {hasActualData && (
          <ScenarioColumn $variant="actual">
            <ScenarioHeader>
              <span>🏠 {t('solarComparison.actualConditions')}</span>
              <Badge variant="info">{t('solarComparison.realistic')}</Badge>
            </ScenarioHeader>

            <ScenarioMetric>
              <MetricLabelSmall>
                {t('solarComparison.suitableRoofArea')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'energy.solarComparison.tooltips.suitableRoofArea.title'
                    )}
                    shortDescription={t(
                      'energy.solarComparison.tooltips.suitableRoofArea.description'
                    )}
                    calculation={t(
                      'energy.solarComparison.tooltips.suitableRoofArea.calculation'
                    )}
                    importance={t(
                      'energy.solarComparison.tooltips.suitableRoofArea.importance'
                    )}
                    limitations={t(
                      'energy.solarComparison.tooltips.suitableRoofArea.limitations'
                    )}
                    source={t(
                      'energy.solarComparison.tooltips.suitableRoofArea.source'
                    )}
                  />
                </InfoTooltip>
              </MetricLabelSmall>
              <MetricValueLarge>
                {Math.round(solar_area).toLocaleString()} m²
              </MetricValueLarge>
            </ScenarioMetric>

            {solar_global_exposure && (
              <ScenarioMetric>
                <MetricLabelSmall>
                  {t('solarComparison.solarIrradiance')}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'energy.solarComparison.tooltips.solarIrradiance.title'
                      )}
                      shortDescription={t(
                        'energy.solarComparison.tooltips.solarIrradiance.description'
                      )}
                      calculation={t(
                        'energy.solarComparison.tooltips.solarIrradiance.calculation'
                      )}
                      importance={t(
                        'energy.solarComparison.tooltips.solarIrradiance.importance'
                      )}
                      limitations={t(
                        'energy.solarComparison.tooltips.solarIrradiance.limitations'
                      )}
                      source={t(
                        'energy.solarComparison.tooltips.solarIrradiance.source'
                      )}
                    />
                  </InfoTooltip>
                </MetricLabelSmall>
                <MetricValueLarge>
                  {Math.round(solar_global_exposure).toLocaleString()}{' '}
                  kWh/m²/year
                </MetricValueLarge>
              </ScenarioMetric>
            )}

            <ScenarioMetric>
              <MetricLabelSmall>
                {t('solarComparison.annualProductionPotential')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'energy.solarComparison.tooltips.annualProduction.title'
                    )}
                    shortDescription={t(
                      'energy.solarComparison.tooltips.annualProduction.description'
                    )}
                    calculation={t(
                      'energy.solarComparison.tooltips.annualProduction.calculation'
                    )}
                    importance={t(
                      'energy.solarComparison.tooltips.annualProduction.importance'
                    )}
                    limitations={t(
                      'energy.solarComparison.tooltips.annualProduction.limitations'
                    )}
                    source={t(
                      'energy.solarComparison.tooltips.annualProduction.source'
                    )}
                  />
                </InfoTooltip>
              </MetricLabelSmall>
              <MetricValueLarge>
                {Math.round(solar_electrical_efficiency).toLocaleString()}{' '}
                kWh/year
              </MetricValueLarge>
            </ScenarioMetric>

            <ScenarioMetric>
              <MetricLabelSmall>
                {t('solarComparison.estimatedCapacity')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'energy.solarComparison.tooltips.estimatedCapacity.title'
                    )}
                    shortDescription={t(
                      'energy.solarComparison.tooltips.estimatedCapacity.description'
                    )}
                    calculation={t(
                      'energy.solarComparison.tooltips.estimatedCapacity.calculation'
                    )}
                    importance={t(
                      'energy.solarComparison.tooltips.estimatedCapacity.importance'
                    )}
                    limitations={t(
                      'energy.solarComparison.tooltips.estimatedCapacity.limitations'
                    )}
                    source={t(
                      'energy.solarComparison.tooltips.estimatedCapacity.source'
                    )}
                  />
                </InfoTooltip>
              </MetricLabelSmall>
              <MetricValueLarge>
                {estimatedCapacityActual.toFixed(1)} kWp
              </MetricValueLarge>
            </ScenarioMetric>
          </ScenarioColumn>
        )}

        {/* Optimal Conditions */}
        {hasOptimalData && (
          <ScenarioColumn $variant="optimal">
            <ScenarioHeader>
              <span>⭐ {t('solarComparison.optimalConditions')}</span>
              <Badge variant="warning">{t('solarComparison.bestCase')}</Badge>
            </ScenarioHeader>

            <ScenarioMetric>
              <MetricLabelSmall>
                {t('solarComparison.bestSuitableArea')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'energy.solarComparison.tooltips.bestSuitableArea.title'
                    )}
                    shortDescription={t(
                      'energy.solarComparison.tooltips.bestSuitableArea.description'
                    )}
                    calculation={t(
                      'energy.solarComparison.tooltips.bestSuitableArea.calculation'
                    )}
                    importance={t(
                      'energy.solarComparison.tooltips.bestSuitableArea.importance'
                    )}
                    limitations={t(
                      'energy.solarComparison.tooltips.bestSuitableArea.limitations'
                    )}
                    source={t(
                      'energy.solarComparison.tooltips.bestSuitableArea.source'
                    )}
                  />
                </InfoTooltip>
              </MetricLabelSmall>
              <MetricValueLarge>
                {Math.round(solar_area_best).toLocaleString()} m²
              </MetricValueLarge>
            </ScenarioMetric>

            {solar_global_exposure_best && (
              <ScenarioMetric>
                <MetricLabelSmall>
                  {t('solarComparison.bestSolarIrradiance')}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'energy.solarComparison.tooltips.bestSolarIrradiance.title'
                      )}
                      shortDescription={t(
                        'energy.solarComparison.tooltips.bestSolarIrradiance.description'
                      )}
                      calculation={t(
                        'energy.solarComparison.tooltips.bestSolarIrradiance.calculation'
                      )}
                      importance={t(
                        'energy.solarComparison.tooltips.bestSolarIrradiance.importance'
                      )}
                      limitations={t(
                        'energy.solarComparison.tooltips.bestSolarIrradiance.limitations'
                      )}
                      source={t(
                        'energy.solarComparison.tooltips.bestSolarIrradiance.source'
                      )}
                    />
                  </InfoTooltip>
                </MetricLabelSmall>
                <MetricValueLarge>
                  {Math.round(solar_global_exposure_best).toLocaleString()}{' '}
                  kWh/m²/year
                </MetricValueLarge>
              </ScenarioMetric>
            )}

            <ScenarioMetric>
              <MetricLabelSmall>
                {t('solarComparison.bestAnnualProduction')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'energy.solarComparison.tooltips.bestAnnualProduction.title'
                    )}
                    shortDescription={t(
                      'energy.solarComparison.tooltips.bestAnnualProduction.description'
                    )}
                    calculation={t(
                      'energy.solarComparison.tooltips.bestAnnualProduction.calculation'
                    )}
                    importance={t(
                      'energy.solarComparison.tooltips.bestAnnualProduction.importance'
                    )}
                    limitations={t(
                      'energy.solarComparison.tooltips.bestAnnualProduction.limitations'
                    )}
                    source={t(
                      'energy.solarComparison.tooltips.bestAnnualProduction.source'
                    )}
                  />
                </InfoTooltip>
              </MetricLabelSmall>
              <MetricValueLarge>
                {Math.round(solar_electrical_efficiency_best).toLocaleString()}{' '}
                kWh/year
              </MetricValueLarge>
            </ScenarioMetric>

            <ScenarioMetric>
              <MetricLabelSmall>
                {t('solarComparison.estimatedCapacity')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'energy.solarComparison.tooltips.estimatedCapacityOptimal.title'
                    )}
                    shortDescription={t(
                      'energy.solarComparison.tooltips.estimatedCapacityOptimal.description'
                    )}
                    calculation={t(
                      'energy.solarComparison.tooltips.estimatedCapacityOptimal.calculation'
                    )}
                    importance={t(
                      'energy.solarComparison.tooltips.estimatedCapacityOptimal.importance'
                    )}
                    limitations={t(
                      'energy.solarComparison.tooltips.estimatedCapacityOptimal.limitations'
                    )}
                    source={t(
                      'energy.solarComparison.tooltips.estimatedCapacityOptimal.source'
                    )}
                  />
                </InfoTooltip>
              </MetricLabelSmall>
              <MetricValueLarge>
                {estimatedCapacityOptimal.toFixed(1)} kWp
              </MetricValueLarge>
            </ScenarioMetric>
          </ScenarioColumn>
        )}
      </ComparisonContainer>

      {hasActualData && hasOptimalData && (
        <NoteBox>
          <strong>⚠️ {t('solarComparison.noteTitle')}</strong>
          <br />
          {t('solarComparison.noteDescription')}
        </NoteBox>
      )}
    </Card>
  )
}
